import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Check, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable/index';
import { toast } from 'sonner';
import MindfullLogo from '@/components/MindfullLogo';

const Login = () => {
  const { user, isLoading, signIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasAccess = searchParams.get('access') === 'true';
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [error, setError] = useState('');
  const [showLogin, setShowLogin] = useState(hasAccess);
  
  // Waitlist state
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Redirect logged-in users to canvas
  useEffect(() => {
    if (!isLoading && user) {
      navigate('/canvas');
    }
  }, [user, isLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }
    
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setIsSigningIn(true);
    const { error: signInError } = await signIn(email, password);
    
    if (signInError) {
      if (signInError.message.includes('Invalid login credentials')) {
        setError('Invalid email or password');
      } else {
        setError(signInError.message);
      }
      setIsSigningIn(false);
    }
  };

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!waitlistEmail.trim()) {
      toast.error('Please enter your email');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(waitlistEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase
        .from('waitlist')
        .insert({ email: waitlistEmail.toLowerCase().trim() });
      
      if (error) {
        if (error.code === '23505') {
          toast.info("You're already on the list!");
          setIsSubmitted(true);
        } else {
          throw error;
        }
      } else {
        setIsSubmitted(true);
        toast.success("You're on the list!");
      }
    } catch (error) {
      console.error('Waitlist signup error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f0eb]">
        <Loader2 className="h-8 w-8 animate-spin text-[#ff5c35]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f0eb] p-4 sm:p-6 lg:p-8">
      <div className="bg-white rounded-[2rem] sm:rounded-[3rem] min-h-[calc(100vh-2rem)] sm:min-h-[calc(100vh-3rem)] lg:min-h-[calc(100vh-4rem)] shadow-xl overflow-hidden flex flex-col">
        {/* Simple Header */}
        <header className="p-6 sm:px-10 lg:px-16">
          <Link to="/" className="inline-flex">
            <MindfullLogo size="md" />
          </Link>
        </header>

        <div className="flex-1 flex items-center justify-center px-4 pb-20">
          <Card className="w-full max-w-md shadow-2xl border-border/50 rounded-3xl">
            <CardHeader className="text-center space-y-4 pb-2">
              <div className="mx-auto w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg border border-border/30">
                <span className="text-3xl font-bold text-foreground">M</span>
              </div>
              <div>
                <CardTitle className="text-2xl font-bold">
                  {showLogin ? 'Welcome back' : 'Join the Waitlist'}
                </CardTitle>
                <CardDescription className="text-base mt-2">
                  {showLogin 
                    ? 'Sign in to access your creative workspace'
                    : "Mindfull is currently in private beta. Join the waitlist to get early access."
                  }
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="pt-6 pb-8 px-8">
              {showLogin ? (
                <>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isSigningIn}
                        className="rounded-xl"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={isSigningIn}
                        className="rounded-xl"
                      />
                    </div>

                    {error && (
                      <p className="text-sm text-destructive">{error}</p>
                    )}

                    <Button
                      type="submit"
                      className="w-full h-12 text-base font-medium rounded-full bg-[#ff5c35] hover:bg-[#e54d2a]"
                      disabled={isSigningIn}
                    >
                      {isSigningIn ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          Signing in...
                        </>
                      ) : (
                        'Sign In'
                      )}
                    </Button>
                  </form>

                  <div className="flex items-center justify-between mt-6">
                    <button 
                      onClick={() => setShowLogin(false)}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      ← Back to waitlist
                    </button>
                    <a 
                      href="/admin"
                      className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                    >
                      Admin
                    </a>
                  </div>
                </>
              ) : (
                <>
                  {isSubmitted ? (
                    <div className="text-center py-4">
                      <div className="w-12 h-12 bg-[#ff5c35] rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2">You're on the list!</h3>
                      <p className="text-muted-foreground text-sm">
                        We'll notify you when Mindfull is ready.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="waitlist-email">Email</Label>
                        <Input
                          id="waitlist-email"
                          type="email"
                          placeholder="you@example.com"
                          value={waitlistEmail}
                          onChange={(e) => setWaitlistEmail(e.target.value)}
                          disabled={isSubmitting}
                          className="rounded-xl"
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full h-12 text-base font-medium gap-2 rounded-full bg-[#ff5c35] hover:bg-[#e54d2a]"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Joining...
                          </>
                        ) : (
                          <>
                            Join Waitlist
                            <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </Button>
                    </form>
                  )}

                  <button 
                    onClick={() => setShowLogin(true)}
                    className="text-sm text-muted-foreground text-center mt-6 block w-full hover:text-foreground transition-colors"
                  >
                    Already have access? Sign in →
                  </button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
